/**
 * Insurance Lead Filters Component
 * Filter controls for the lead queue (status, location, my leads)
 */

import React from 'react';
import { LeadStatus, LEAD_STATUS_CONFIG } from '../../types/insurance';

interface InsuranceLeadFiltersProps {
  status: LeadStatus | 'all';
  locationId: number | null;
  myLeadsOnly: boolean;
  onStatusChange: (status: LeadStatus | 'all') => void;
  onLocationChange: (locationId: number | null) => void;
  onMyLeadsOnlyChange: (myLeadsOnly: boolean) => void;
  locations: Array<{ id: number; name: string }>;
}

export const InsuranceLeadFilters: React.FC<InsuranceLeadFiltersProps> = ({
  status,
  locationId,
  myLeadsOnly,
  onStatusChange,
  onLocationChange,
  onMyLeadsOnlyChange,
  locations,
}) => {
  const statusOptions: Array<{ value: LeadStatus | 'all'; label: string }> = [
    { value: 'all', label: 'All Statuses' },
    { value: 'unclaimed', label: LEAD_STATUS_CONFIG.unclaimed.label },
    { value: 'claimed', label: LEAD_STATUS_CONFIG.claimed.label },
    { value: 'completed', label: LEAD_STATUS_CONFIG.completed.label },
    { value: 'discarded', label: LEAD_STATUS_CONFIG.discarded.label },
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
      <div className="flex flex-wrap items-center gap-4">
        {/* Status Filter */}
        <div className="flex-1 min-w-[180px]">
          <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            id="status-filter"
            value={status}
            onChange={(e) => onStatusChange(e.target.value as LeadStatus | 'all')}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          >
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Location Filter */}
        <div className="flex-1 min-w-[180px]">
          <label htmlFor="location-filter" className="block text-sm font-medium text-gray-700 mb-1">
            Location
          </label>
          <select
            id="location-filter"
            value={locationId ?? ''}
            onChange={(e) => onLocationChange(e.target.value ? parseInt(e.target.value, 10) : null)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          >
            <option value="">All Locations</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </select>
        </div>

        {/* My Leads Toggle */}
        <div className="flex items-center pt-6">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={myLeadsOnly}
              onChange={(e) => onMyLeadsOnlyChange(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            <span className="ml-3 text-sm font-medium text-gray-700">My Leads Only</span>
          </label>
        </div>

        {/* Quick Filters */}
        <div className="flex items-center gap-2 pt-6 ml-auto">
          <button
            onClick={() => {
              onStatusChange('unclaimed');
              onMyLeadsOnlyChange(false);
            }}
            className="px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
          >
            Available
          </button>
          <button
            onClick={() => {
              onStatusChange('claimed');
              onMyLeadsOnlyChange(true);
            }}
            className="px-3 py-1.5 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-md transition-colors"
          >
            My Queue
          </button>
        </div>
      </div>
    </div>
  );
};

export default InsuranceLeadFilters;
